import React, { useState, useEffect } from 'react';
import apiClient from '../../apiClient';
import { FiEdit, FiTrash2, FiCopy, FiPlus } from 'react-icons/fi';
import Table from '../common/Table/Table';
import '../common/Table/Table.css';

const Status = ({ statusDetails, setMessage }) => {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [formData, setFormData] = useState({ status: '' });

  useEffect(() => {
    setStatuses(statusDetails);
    setLoading(false);
  }, [statusDetails]);

  useEffect(() => {
    fetchStatuses();
  }, []);

  const fetchStatuses = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/api/status');
      setStatuses(response.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (status) => {
    setCurrentStatus(status);
    setFormData({ status: status.status });
    setShowForm(true);
  };

  const handleDeleteClick = async (id) => {
    if (!window.confirm('Are you sure you want to delete this status?')) return;
    try {
      await apiClient.delete(`/api/status/${id}`);
      setMessage({ text: 'Status deleted successfully', type: 'success' });
      fetchStatuses();
    } catch (err) {
      setMessage({ text: err.response?.data?.error || 'Failed to delete status', type: 'error' });
    }
  };

  const handleCopyClick = (status) => {
    navigator.clipboard.writeText(status.status);
    setMessage({ text: 'Status copied to clipboard', type: 'success' });
  };

  const handleFormChange = (e) => {
    setFormData({ status: e.target.value });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.status.trim()) {
      setMessage({ text: 'Status cannot be empty', type: 'error' });
      return;
    }
    try {
      if (currentStatus) {
        await apiClient.put(`/api/status/${currentStatus.id}`, { status: formData.status });
        setMessage({ text: 'Status updated successfully', type: 'success' });
      } else {
        await apiClient.post('/api/status', { status: formData.status });
        setMessage({ text: 'Status added successfully', type: 'success' });
      }
      setShowForm(false);
      setCurrentStatus(null);
      setFormData({ status: '' });
      fetchStatuses();
    } catch (err) {
      setMessage({ text: err.response?.data?.error || 'Failed to save status', type: 'error' });
    }
  };

  const handleAddNew = () => {
    setCurrentStatus(null);
    setFormData({ status: '' });
    setShowForm(true);
  };

  if (loading) return <div className="loading">Loading statuses...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  const columns = [
    { header: 'ID' },
    { header: 'Status' },
    { header: 'Created At' },
    { header: 'Updated At' },
    { header: 'Actions', className: 'actions-header' }
  ];

  const renderRow = (status) => (
    <>
      <td>{status.id}</td>
      <td>{status.status}</td>
      <td>{new Date(status.created_at).toLocaleString()}</td>
      <td>{new Date(status.updated_at).toLocaleString()}</td>
      <td className="actions-cell">
        <button
          onClick={() => handleEditClick(status)}
          className="lead-edit-btn"
          title="Edit status"
        >
          <FiEdit />
        </button>
        <button
          onClick={() => handleCopyClick(status)}
          className="copy-btn"
          title="Copy status"
        >
          <FiCopy />
        </button>
        <button
          onClick={() => handleDeleteClick(status.id)}
          className="delete-btn"
          title="Delete status"
        >
          <FiTrash2 />
        </button>
      </td>
    </>
  );

  return (
    <div className="package-tab">
      <div className="package-header">
        <h2>Status Management</h2>
        <button onClick={handleAddNew} className="add-btn">
          <FiPlus /> Add New Status
        </button>
      </div>

      {showForm && (
        <form className="modal-form" onSubmit={handleFormSubmit}>
          <div className="form-group">
            <label>Status:</label>
            <input
              type="text"
              value={formData.status}
              onChange={handleFormChange}
              autoFocus
            />
          </div>
          <div className="modal-buttons">
            <button type="submit" className="save-button">
              {currentStatus ? 'Update' : 'Add'}
            </button>
            <button
              type="button"
              className="cancel-button"
              onClick={() => {
                setShowForm(false);
                setCurrentStatus(null);
                setFormData({ status: '' });
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <Table 
        columns={columns} 
        data={statuses} 
        renderRow={renderRow} 
        className="package-table"
      />
    </div>
  );
};

export default Status;
